import {
  useSafetyAudit,
  useSafetyVerdict,
  type SafetyAuditEntryOut,
} from '@/api/endpoints/safety'
import { Chip, type ChipVariant } from '@/components/ui/Chip'
import {
  buildVerdictSummaryPt,
  layerDescription,
  layerOutcomeLabel,
  layerTitle,
  translateCategories,
  translateStatus,
  type LayerOutcome,
} from '@/features/safety/lib/i18n'
import styles from './SafetyDetailPanel.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

export interface SafetyDetailPanelProps {
  videoId: string
  lang: string
  /**
   * When false, the panel doesn't fetch — used by parents that mount the
   * component once and toggle visibility, so we avoid redundant requests
   * when collapsed. Defaults to true.
   */
  enabled?: boolean
}

/**
 * Shared "what does the safety scan say" block used by both the Ranking
 * page (expanded row) and the Safety page (expanded row). Renders the
 * verdict header plus one card per safety layer (blocklist, llm_judge)
 * showing what each one returned in the most recent scan.
 */
export function SafetyDetailPanel({ videoId, lang, enabled = true }: SafetyDetailPanelProps) {
  const verdict = useSafetyVerdict({ videoId, lang, enabled })
  const audit = useSafetyAudit({ videoId, lang, enabled })

  if (verdict.isPending) {
    return <p className={styles.muted}>Carregando detalhamento...</p>
  }
  if (verdict.isError) {
    return (
      <QueryErrorNotice
        compact
        error={verdict.error}
        label="o detalhamento"
        onRetry={() => {
          void verdict.refetch()
        }}
      />
    )
  }
  const v = verdict.data

  const latestPerLayer = pickLatestPerLayer(audit.data ?? [])
  // Order matches SafetyLayer enum in backend/src/db/models/_base.py, minus
  // openai_moderation (retired in v5 — old audit rows just don't get a card).
  // llm_judge_clips runs only at pre_compose; show it when an audit row exists.
  const layerOrder: string[] = ['blocklist', 'llm_judge']
  if (latestPerLayer['llm_judge_clips']) layerOrder.push('llm_judge_clips')

  return (
    <div className={styles.panel}>
      <div className={styles.headRow}>
        <Chip variant={chipForStatus(v.status)}>{translateStatus(v.status)}</Chip>
        {v.checked_at ? (
          <span className={styles.metaInline}>
            Verificado em {formatDate(v.checked_at)}
          </span>
        ) : null}
      </div>

      <p className={styles.summary}>{buildVerdictSummaryPt(v)}</p>

      <div className={styles.layerStack}>
        {audit.isPending ? (
          <p className={styles.muted}>Carregando camadas...</p>
        ) : audit.isError ? (
          <QueryErrorNotice
            compact
            error={audit.error}
            label="as camadas"
            onRetry={() => {
              void audit.refetch()
            }}
          />
        ) : (
          layerOrder.map((layerKey) => (
            <LayerCard
              key={layerKey}
              layer={layerKey}
              entry={latestPerLayer[layerKey]}
              isBlockingLayer={v.blocking_layer === layerKey}
            />
          ))
        )}
      </div>

      {v.reason ? (
        <details className={styles.rawReason}>
          <summary className={styles.rawReasonSummary}>Mensagem técnica (em inglês)</summary>
          <p className={styles.rawReasonText}>{v.reason}</p>
        </details>
      ) : null}
    </div>
  )
}

interface LayerCardProps {
  layer: string
  entry: SafetyAuditEntryOut | undefined
  isBlockingLayer: boolean
}

function LayerCard({ layer, entry, isBlockingLayer }: LayerCardProps) {
  const outcome = computeOutcome(entry)
  const variant = chipForOutcome(outcome)
  const description = layerDescription(layer)
  const evidence = entry?.evidence.trim() ?? ''
  const categories = entry?.categories ?? []
  const translatedCategories = translateCategories(categories)
  const errorHint = outcome === 'errored' ? errorHintPt(entry?.error ?? null) : null

  return (
    <div
      className={[
        styles.layerCard,
        isBlockingLayer ? styles.layerCardBlocking : '',
      ].join(' ').trim()}
    >
      <div className={styles.layerHead}>
        <Chip variant={variant}>{layerOutcomeLabel(outcome)}</Chip>
        <strong className={styles.layerTitle}>{layerTitle(layer)}</strong>
        {entry?.model ? <span className={styles.layerModel}>{entry.model}</span> : null}
      </div>

      {description ? <p className={styles.layerDescription}>{description}</p> : null}

      {errorHint ? <p className={styles.layerError}>{errorHint}</p> : null}

      {translatedCategories.length > 0 ? (
        <div className={styles.layerChips}>
          {translatedCategories.map((label, i) => {
            const original = categories[i] ?? label
            return (
              <Chip
                key={`${layer}-${original}`}
                variant="warning"
                title={`Original: ${original}`}
              >
                {label}
              </Chip>
            )
          })}
        </div>
      ) : null}

      {evidence ? (
        <blockquote className={styles.layerEvidence}>“{evidence}”</blockquote>
      ) : outcome === 'not_run' ? (
        <p className={styles.layerEmpty}>Esta camada não rodou no último scan.</p>
      ) : outcome === 'passed' ? (
        <p className={styles.layerEmpty}>Nada suspeito encontrado.</p>
      ) : null}

      {entry ? (
        <div className={styles.layerFoot}>
          <span>{formatDate(entry.created_at)}</span>
          <span>{entry.latency_ms} ms</span>
        </div>
      ) : null}
    </div>
  )
}

function errorHintPt(error: string | null): string | null {
  if (!error) return 'A camada não conseguiu rodar. Veja os logs do backend.'
  if (error === 'timeout') {
    return 'A camada estourou o timeout. Tente um rescan.'
  }
  if (error === 'parse_failed') {
    return 'A resposta do modelo não foi parseável. Tente um rescan.'
  }
  if (error.startsWith('api_error:')) {
    return `Erro da API: ${error.slice('api_error:'.length).trim()}.`
  }
  return `Erro: ${error}`
}

function pickLatestPerLayer(
  rows: SafetyAuditEntryOut[],
): Record<string, SafetyAuditEntryOut> {
  // Audit is returned newest-first by the API, so the first hit per layer
  // is the most recent one. A second pass per layer would be O(n*L); a
  // single pass with a guard is enough.
  const out: Record<string, SafetyAuditEntryOut> = {}
  for (const row of rows) {
    if (!out[row.layer]) {
      out[row.layer] = row
    }
  }
  return out
}

function computeOutcome(entry: SafetyAuditEntryOut | undefined): LayerOutcome {
  if (!entry) return 'not_run'
  if (entry.blocked) return 'blocked'
  // Backend now sets `error` when a layer reaches a non-decision early exit
  // (no API key, timeout, parse failure). Treat any non-empty error as
  // "errored" so the user sees "Erro" instead of a misleading "Passou".
  if (entry.error) return 'errored'
  return 'passed'
}

function chipForStatus(status: string): ChipVariant {
  switch (status) {
    case 'passed':
      return 'success'
    case 'blocked':
    case 'error':
      return 'danger'
    case 'needs_review':
    case 'pending':
    case 'never_checked':
      return 'warning'
    default:
      return 'default'
  }
}

function chipForOutcome(outcome: LayerOutcome): ChipVariant {
  switch (outcome) {
    case 'passed':
      return 'success'
    case 'blocked':
      return 'danger'
    case 'errored':
      return 'warning'
    case 'not_run':
      return 'default'
  }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
